import {ZodError} from 'zod';

export interface ContactsResponse {
    total: number;
    results?: Contact[];
}

export interface TablePaging {
    total: number;
    currentPage: number;
    pageSize: number;
}

export interface Contact {
    id: string;
    address?: null;
    city?: null;
    company?: null;
    email: string;
    firstName?: string;
    lastName?: string;
    phone: string;
    state: string;
    zip?: null;
    ownerId: string;
    owner?: Owner;
    createdAt: string;
    leadStatus: string;
    formCount: number;
    notes?: string;
}

export enum CustomerPortalPipelineSteps {
    Walkthrough = 'walkthrough',
}
export interface CustomerPortalQuote {
    email: string;
    quote: unknown | Record<string, any>;
}

export type PaymentInformation = {
    id?: string;
    index?: number;
    label: string;
    routingNumber: string;
    accountNumber: string;
    paymentFrequency: string;
    dayOfMonth: string;
    plaidAccountId: string;
    updatedAt: string;
    ownerName: string;
    institutionName: string;
    accountType: AccountType;
    conditionalCoverage: boolean;
};

export interface CustomerPortal {
    id?: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    pipelineStep: CustomerPortalPipelineSteps;
    consent?: string;
    dateOfBirth?: string;
    paymentInformations?: PaymentInformation[];
}

export interface Owner {
    portalId: number;
    ownerId: number;
    type: string;
    firstName: string;
    lastName: string;
    email: string;
    createdAt: number;
    updatedAt: number;
    remoteList?: RemoteListEntity[];
    hasContactsAccess: boolean;
    activeUserId: number;
    userIdIncludingInactive: number;
    activeSalesforceId?: null;
    isActive: boolean;
}

export interface RemoteListEntity {
    id: number;
    portalId: number;
    ownerId: number;
    remoteId: string;
    remoteType: string;
    active: boolean;
}

export interface RecordsEntity {
    v: V;
    s: string;
    p: string;
}

export interface V {
    ageniumCaseId: string;
    dealId: string;
    formCreatedAt: Date | null;
    formInitiallySubmittedAt?: Date | null;
    formid: string;
    paramJS: string;
    contact: string;
    name: string;
    health?: HealthEntity[];
    carriers?: CarriersEntity[];
    parameters: ParametersEntity[];
    version: string;
    products?: ProductsEntity[];
    timestamp: number;
    customerPortalCustomerID: string;
    hubspot?: {
        hs_object_id: string;
        hubspot_owner_id: string;
    };
}

export interface HealthEntity {
    core?: boolean;
    uiWidth: number;
    drugs?: string[];
    guard: string;
    name: string;
    label: string;
    row: number;
    id: string;
    type: string;
    category: string;
    value: boolean | string;
    options?: string[];
}

export interface CarriersEntity {
    buildtables?: BuildtablesEntity[];
    name: string;
    health?: HealthEntityOrTobaccoEntity[];
    tobacco?: HealthEntityOrTobaccoEntity[];
    id: string;
    minincome: number;
    products?: CarrierProducts[];
}

export interface BuildtablesEntity {
    uiWidth: number;
    guard: string;
    name: string;
    type: string;
    category: string;
    value: string;
    table: Table;
}

export interface Table {
    xparam: string;
    categories?: string[];
    yparam: string;
    values?: number[][];
}

export interface HealthEntityOrTobaccoEntity {
    rating: string;
    rule: string;
}

export interface CarrierProducts {
    name: string;
    state?: State;
    type: string;
    maxage?: number;
    agetype?: string;
    minage?: number;
}

export interface State {
    exclude: string;
}

export interface ParametersEntity {
    preapproval: boolean;
    guard?: string;
    description?: string;
    prequalification: boolean;
    label?: string;
    type: string;
    uiWidth?: number;
    name: string;
    options?: string[];
    walkthrough: boolean;
    id: string;
    category: string;
    value: string | boolean | number;
    table?: Table1;
    required?: boolean;
    core?: boolean;
}

export interface Table1 {
    xparam: string;
    categories?: string[];
    yparam: string;
    values?: number[][];
}

export interface ProductsEntity {
    rating: string;
    carrier: string;
    id: string;
    category: string;
    type: string;
    criteria?: string[];
}

export enum ProductGuaranteedValues {
    gerber = 'Gerber: Final Expense',
    great_western = 'Great Western: Final Expense',
    transamerica = 'Transamerica: Easy Solution',
    moo = 'Mutual of Omaha: Living Promise',
}

export enum ProductGuaranteedFileds {
    productGuaranteed = 'productGuaranteed',
    gender = 'gender',
    age = 'age',
    bday = 'bday',
    bmonth = 'bmonth',
    byear = 'byear',
    state = 'state',
    specifiedFaceAmount = 'specifiedFaceAmount',
    premiumFrequency = 'premiumFrequency',
    specifiedFaceAmountGW = 'specifiedFaceAmountGW',
    specifiedFaceAmountTransGerber = 'specifiedFaceAmountTransGerber',
    specifiedFaceAmountMOO = 'specifiedFaceAmountMOO',

    termPeriod = 'termPeriod',
    smokerRatingMOO = 'smokerRatingMOO',
    healthRatingMOO = 'healthRatingMOO',

    nearestAge = 'nearestAge',
}

export type NargsHashMapType = {
    [name: string]: {
        value: number | string;
    };
};

export enum ProductGuaranteedIXNMOORequestFields {
    state = 'state',
    gender = 'gender',
    product_types = 'product_types[]',
    face_amount = 'face_amount',
    tobacco = 'tobacco',
    carrier_ids = 'carrier_ids[]',
    current_age = 'current_age',
    nearest_age = 'nearest_age',
    health_categories = 'health_categories[]',
    table_rates = 'table_rates[]',
}

export enum ProductTypes {
    term = 'Term',
    iul = 'IUL',
    gul = 'GUL',
    guaranteed = 'Guaranteed',
    vul = 'VUL',
    lswiul = 'LSWIUL',
    prosper = 'Prosper',
}

export const PolicyInfoProductTypes = {
    NONE: '',
    TERM_10: '_10yearTerm',
    TERM_15: '_15yearTerm',
    TERM_20: '_20yearTerm',
    TERM_25: '_25yearTerm',
    TERM_30: '_30yearTerm',
    TERM_35: '_35yearTerm',
    TERM_40: '_40yearTerm',
    IUL: 'IUL',
    GI: 'GuaranteedIssue',
    GUL: 'GUL',
    VUL: 'VUL',
    ACCIDENTAL: 'Accidental',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyInfoProductTypes = (typeof PolicyInfoProductTypes)[keyof typeof PolicyInfoProductTypes];

export const PolicyInfoCarrierNames = {
    FG: 'F&G',
    EMC: 'EMCNationalLifeCompany',
    AMERITAS: 'Ameritas',
    NATIONAL: 'NationalLifeGroup',
    EQUITABLE: 'Equitable',
    LINCOLN: 'LincolnFinancial',
    NA: 'NorthAmerican',
    PACIFIC: 'PacificLife',
    GERBER: 'Gerber',
    GW: 'GreatWestern',
    MOO: 'MutualofOmaha',
    TA: 'Transamerica',
    ALLIANZ: 'Allianz',
    ASSURITY: 'Assurity',
    PRINCIPAL: 'Principal',
    PROTECTIVE: 'Protective',
    PRUDENTIAL: 'Prudential',
    UHL: 'UnitedHomeLife',
    VIVA: 'VivaLife',
    FGEverlast: 'FGEverlast',
    FGPathsetter: 'FGPathsetter',
};
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyInfoCarrierNames = (typeof PolicyInfoCarrierNames)[keyof typeof PolicyInfoCarrierNames];

export type TemplateAfterSaleQuoteData = {
    carrierQuoted: string;
    productType: PolicyInfoProductTypes;
    monthlyPremium: number;
    coverageAmount: number;
};

export type DocumentType = {
    downloadUrl: string;
    name: string;
};

export type underwritingRequirementType = {
    id?: string;
    type: string;
    customerId?: string;
    status: string;
    private: boolean;
    title?: string;
    link?: string;
    additional_instructions?: string;
    internal_note?: string;
    file_title?: string;
    data?: string;
    documents?: DocumentType[];
    needDocusign?: boolean;
    dsReturnUrl?: string;
    data_json?: string;
    paymentInformationId?: string;
    updatedAt: string;
    requirementId?: string;
};
export type UnderwritingRequirementUpdate = Pick<underwritingRequirementType, 'id' | 'status' | 'internal_note'>;

export type underwritingRequirementNameType =
    | 'id'
    | 'customerId'
    | 'status'
    | 'title'
    | 'link'
    | 'additional_instructions'
    | 'internal_note'
    | 'file_title'
    | 'data'
    | 'type';

export const RequirementStatus = {
    OUTSTANDING: 'outstanding',
    RECEIVED: 'received',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequirementStatus = (typeof RequirementStatus)[keyof typeof RequirementStatus];

export const RequirementPaymentType = {
    PAYMENT_INFORMATION: 'paymentInformation',
    EFT_DOCUMENT: 'eftDocument',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequirementPaymentType = (typeof RequirementPaymentType)[keyof typeof RequirementPaymentType];

export const AccountType = {
    CHECKING: 'checking',
    SAVINGS: 'savings',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AccountType = (typeof AccountType)[keyof typeof AccountType];

export const RequirementPolicyType = {
    POLICY_DOCUMENTS: 'policyDocuments',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RequirementPolicyType = (typeof RequirementPolicyType)[keyof typeof RequirementPolicyType];

export type Severity = 0 | 1;

export interface Message {
    Severity: Severity;
    MessageText: string;
    Code: string;
    Source: string;
}

export interface Field {
    Values: number[];
    Name: string;
    Caption: string;
}

export interface TableLSW {
    RowCount: number;
    Name: string;
    Caption: string;
    Fields: Field[];
}

export interface Model {
    ModelType: number;
    Name: string;
    Caption: string;
    Tables: TableLSW[];
}

export interface ResponseLSW {
    Success: boolean;
    TransactionID: string;
    TransExeDateTime: string;
    Messages: Message[];
    Models: Model[];
    Illustrations: string;
}

export interface ResponseLSWPoll {
    success: boolean;
    data: any;
}

export interface ResponseLSWJob {
    success: boolean;
    data: {id: string};
    message?: string;
    errors?: ZodError[];
}

export interface AdditionalCondition {
    name: string;
    medications: Medication[];
}

export interface Medication {
    name: string;
    dosageQuantity: number;
    dosageUnit: string;
    frequency: string;
    lastDoseDate: string;
}

export type InvestmentGoal = 'savings' | 'incomeReplacement' | 'inheritance' | 'healthEmergency' | 'debtPayment';
